import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Play, Pause, RotateCcw, Save, Plus, Zap, List, Activity, Server, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { io } from 'socket.io-client';

const Section = ({ title, icon: Icon, children, actions }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200">
    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
      <div className="flex items-center space-x-2">
        {Icon ? <Icon className="w-4 h-4 text-blue-600" /> : null}
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="flex items-center space-x-2">{actions}</div>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const ServiceCard = ({ id, service, onStart, onStop, onRestart, onSaveConfig }) => {
  const [interval, setIntervalVal] = useState(service.intervalMinutes || 5);
  useEffect(() => setIntervalVal(service.intervalMinutes || 5), [service.intervalMinutes]);

  const stats = service.stats || {};
  const isHistorical = id === 'historicalImport';

  return (
    <div className="border border-gray-100 rounded-xl p-4 bg-white shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex flex-col">
          <div className="flex items-center space-x-2">
            <Server className={`w-5 h-5 ${service.running ? 'text-green-600' : 'text-gray-400'}`} />
            <span className="text-base font-bold text-gray-800">{service.name}</span>
            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${service.running ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600'}`}>
              {service.running ? 'Online' : 'Offline'}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1 leading-tight">{service.description}</p>
        </div>
        <div className="flex items-center space-x-1">
          <button
            title="Start"
            onClick={onStart}
            disabled={service.running && !isHistorical}
            className={`p-1.5 rounded-lg transition-colors ${service.running && !isHistorical ? 'text-gray-300 cursor-not-allowed' : 'text-green-600 hover:bg-green-50'}`}
          >
            <Play className="w-4 h-4" />
          </button>
          <button
            title="Stop"
            onClick={onStop}
            disabled={!service.running}
            className={`p-1.5 rounded-lg transition-colors ${!service.running ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            <Pause className="w-4 h-4" />
          </button>
          <button
            title="Restart"
            onClick={onRestart}
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        {!isHistorical && (
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-[10px] uppercase font-bold text-slate-400 mb-1">Configuración</div>
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min={1}
                  value={interval}
                  onChange={(e) => setIntervalVal(parseInt(e.target.value || '1', 10))}
                  className="w-14 px-2 py-1 text-xs border rounded bg-white font-medium"
                />
                <span className="text-[10px] text-slate-500">min</span>
              </div>
              <button
                onClick={() => onSaveConfig({ intervalMinutes: interval })}
                className="w-full py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-[10px] font-bold flex items-center justify-center transition-colors"
              >
                <Save className="w-3 h-3 mr-1" /> APLICAR
              </button>
            </div>
          </div>
        )}

        <div className={`bg-slate-50 rounded-lg p-3 ${isHistorical ? 'col-span-2' : ''}`}>
          <div className="text-[10px] uppercase font-bold text-slate-400 mb-1">Actividad</div>
          <div className="mt-1 text-[11px] text-slate-700 space-y-1 overflow-hidden">
            {Object.keys(stats).length === 0 ? (
              <div className="text-slate-400 italic">No hay logs recientes</div>
            ) : (
              Object.entries(stats).slice(0, 4).map(([k, v]) => (
                <div key={k} className="flex justify-between items-center border-b border-white pb-0.5 last:border-0">
                  <span className="text-slate-400 truncate mr-2" title={k}>{k.replace(/([A-Z])/g, ' $1').toLowerCase()}</span>
                  <span className="font-bold whitespace-nowrap">
                    {typeof v === 'object' && v !== null
                      ? (Array.isArray(v) ? `[${v.length}]` : `{${Object.keys(v).length}}`)
                      : String(v ?? '')}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AutomationDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [subs, setSubs] = useState([]);
  const [topicsInput, setTopicsInput] = useState('public.siigoapi.products.stock.update');
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [siigoLogs, setSiigoLogs] = useState([]);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/monitor/status');
      if (res.data.success) {
        setStatus(res.data.data);
        setWebhookLogs((res.data.data.logs?.webhook_logs) || []);
        setSiigoLogs((res.data.data.logs?.siigo_logs) || []);
      }
    } catch (e) {
      toast.error('Error cargando estado de servicios');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWebhooks = useCallback(async () => {
    try {
      const res = await api.get('/monitor/webhooks');
      if (res.data.success) {
        setSubs(res.data.data.subscriptions || []);
      }
    } catch (e) {
      toast.error('Error cargando suscripciones');
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadWebhooks();

    const socket = io('/', {
      path: '/socket.io',
      withCredentials: true,
      transports: ['websocket', 'polling']
    });
    return () => {
      try { socket.disconnect(); } catch { }
    };
  }, [loadStatus, loadWebhooks]);

  const action = async (path, body, okMsg) => {
    try {
      const res = await api.post(path, body || {});
      if (res.data.success) {
        toast.success(okMsg || 'Acción ejecutada');
        await loadStatus();
      } else {
        toast.error(res.data.message || 'Acción falló');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Error en la acción');
    }
  };

  const subscribeTopics = async () => {
    const topics = topicsInput.split(',').map(s => s.trim()).filter(Boolean);
    for (const t of topics) {
      await action('/monitor/webhooks/subscribe', { topic: t }, `Suscrito a ${t}`);
    }
    await loadWebhooks();
  };

  const testWebhook = async () => {
    const topic = topicsInput.split(',').map(s => s.trim()).filter(Boolean)[0] || 'public.siigoapi.products.stock.update';
    await action('/monitor/test/webhook', { topic }, `Test webhook enviado (${topic})`);
  };

  if (loading && !status) {
    return (
      <div className="p-8 flex flex-col items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mb-4" />
        <p className="text-gray-500 font-medium">Cargando mando de control...</p>
      </div>
    );
  }

  const services = status?.services || {};

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-600 p-2 rounded-xl shadow-blue-200 shadow-lg">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">Centro de Automatización</h1>
              <p className="text-sm text-slate-500">Mando de control para procesos en segundo plano</p>
            </div>
          </div>
          <button
            onClick={loadStatus}
            className="px-4 py-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl text-sm font-bold flex items-center shadow-sm transition-all active:scale-95"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> REFRESCAR
          </button>
        </div>

        <Section title="Servicios del Sistema" icon={Activity}>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {Object.entries(services).map(([id, s]) => (
              <ServiceCard
                key={id}
                id={id}
                service={s}
                onStart={() => action(`/monitor/service/${id}/start`, null, `${s.name} iniciado`)}
                onStop={() => action(`/monitor/service/${id}/stop`, null, `${s.name} detenido`)}
                onRestart={() => action(`/monitor/service/${id}/restart`, null, `${s.name} reiniciado`)}
                onSaveConfig={(cfg) => action(`/monitor/service/${id}/config`, cfg, 'Configuración aplicada')}
              />
            ))}
          </div>
        </Section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Section title="Webhooks SIIGO" icon={Zap} actions={
            <div className="flex space-x-2">
              <button onClick={subscribeTopics} className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-bold flex items-center transition-colors">
                <Plus className="w-3 h-3 mr-1" /> SUSCRIBIR
              </button>
              <button onClick={testWebhook} className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-xs font-bold flex items-center transition-colors">
                <Zap className="w-3 h-3 mr-1" /> TEST
              </button>
            </div>
          }>
            <div className="mb-4">
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 block">Topics de interés</label>
              <input
                type="text"
                className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                placeholder="public.siigoapi.products.create, public.siigoapi.customers.update"
                value={topicsInput}
                onChange={(e) => setTopicsInput(e.target.value)}
              />
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-100">
              <table className="min-w-full text-xs bg-white">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-400 border-b border-slate-100">
                    <th className="py-3 px-4 font-bold uppercase">ID</th>
                    <th className="py-3 px-4 font-bold uppercase">Topic</th>
                    <th className="py-3 px-4 font-bold uppercase text-center">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {subs.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-8 text-center text-slate-300 italic">No hay suscripciones activas</td>
                    </tr>
                  ) : subs.map((s) => (
                    <tr key={`${s.webhook_id}-${s.topic}`} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4 font-mono text-[10px] text-slate-400">...{s.webhook_id.split('-').pop()}</td>
                      <td className="py-3 px-4 font-medium text-slate-700">{s.topic}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${s.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}`}>
                          {s.active ? 'Activo' : 'Off'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Logs de Sincronización" icon={List}>
            <div className="overflow-x-auto rounded-xl border border-slate-100">
              <table className="min-w-full text-xs bg-white">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-400 border-b border-slate-100">
                    <th className="py-3 px-4 font-bold uppercase">Referencia</th>
                    <th className="py-3 px-4 font-bold uppercase">Estado</th>
                    <th className="py-3 px-4 font-bold uppercase">Fecha</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {siigoLogs.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-8 text-center text-slate-300 italic">Sin registros de sincronización</td>
                    </tr>
                  ) : siigoLogs.map((l) => (
                    <tr key={l.id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4 font-medium text-slate-700">{l.siigo_invoice_id || `Log #${l.id}`}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${l.sync_status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {l.sync_status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-400">{new Date(l.processed_at || l.created_at).toLocaleTimeString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default AutomationDashboardPage;
